import React, { Component, ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts/highstock'
import highMapModule from 'highcharts/modules/map'
import highExporting from 'highcharts/modules/exporting'
import highExportData from 'highcharts/modules/export-data'
import highOfflineExport from 'highcharts/modules/offline-exporting'
import get from 'lodash/get'
import { download } from './symbols'
import {
  DataProvider,
  formType,
  formTypeOld,
  ChartDescription,
  TimeFrameButton,
} from './types'
import {
  formatDateToInputFormat,
  getDateInterval,
  getFramByInteral,
} from './chartHelpers'
import { ExportMenu } from './components/ExportMenu'
import { ColorScheme } from './components/ColorScheme'
import { ChartFormat } from './components/ChartFormat'
import styles from './chart.module.scss'
import { combinedAnalytics } from 'src/utils/common'

const DEFAULT_FRAME = 'all'
let IS_SAFARI

if (typeof Highcharts === 'object') {
  highExporting(Highcharts)
  highMapModule(Highcharts)
  highExportData(Highcharts)
  highOfflineExport(Highcharts)

  Highcharts.SVGRenderer.prototype.symbols.download = download

  IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

const wholePeriodTimestampFrom = IS_SAFARI
  ? new Date('2013/01/01 00:00').getTime()
  : new Date('2013-01-01 00:00').getTime()

const wholePeriodValueFrom = formatDateToInputFormat(wholePeriodTimestampFrom)

interface Props {
  isServer: boolean
  isMobile: boolean
  isShare: boolean
  minDate: number
  height: number
  width: number
  zoomPadding?: number
  mobileZoomPadding?: number
  DataProviders: DataProvider[][]
  navigatorData: DataProvider[]
  id: string
  charts: ChartDescription[]
  additionalButtons?: any
  controlMarginRight?: number
  frames?: TimeFrameButton[]
  onDateChange: (from: number, to: number) => Promise<DataProvider[]>
  generateExportConfig: (
    colorScheme: ColorScheme,
    chartFormat: ChartFormat,
    from: string,
    to: string,
    yAxisConf: any
  ) => any
  generateConfig: (
    data: DataProvider[][],
    navigatorSeries: DataProvider[],
    enabledCharts: string[],
    choosenFrame: string,
    fullScreen: boolean,
    height: number,
    width: number
  ) => any
  isDownloadDropdownInverted?: boolean
}

interface State {
  choosenFrame: string
  isZoomedMax: boolean
  isZoomed: boolean
  isChartHidden: boolean
  inputInFocus: formType | null
  fullScreen: boolean
  isServer: boolean
  valueFrom: string
  valueTo: string
  valueToOldValue: string | any
  valueFromOldValue: string | any
  disabledCharts: string[]
  defaultFromTimestamp: number
  defaultToTimestamp: number
}

const DEFAULT_FRAMES = [
  { period: '1d', frame: '5m' },
  { period: '7d', frame: '15m' },
  { period: '1m', frame: '1h' },
  { period: '3m', frame: '2h' },
  { period: '1y', frame: '24h' },
  { period: 'all', frame: 'all' },
]

export class TimeLineChart extends Component<any, State> {
  ref: React.RefObject<any>
  containerRef: React.RefObject<any>
  refInputFirst: React.RefObject<HTMLInputElement>
  refInputSecond: React.RefObject<HTMLInputElement>
  el?: HTMLDivElement
  isFullScreenDefault: boolean
  afterSetExtremesTimeout?: number
  dateFrom = ''
  dateTo = ''

  constructor(props: Props) {
    super(props)

    this.state = {
      choosenFrame: DEFAULT_FRAME,
      isZoomedMax: false,
      isZoomed: false,
      fullScreen: this.isMobileLandscape(),
      isChartHidden: false,
      inputInFocus: null,
      isServer: true,
      valueTo: formatDateToInputFormat(Date.now()),
      valueFrom: wholePeriodValueFrom,
      valueToOldValue: formatDateToInputFormat(Date.now()),
      valueFromOldValue: wholePeriodValueFrom,
      disabledCharts: [],
      defaultFromTimestamp: wholePeriodTimestampFrom,
      defaultToTimestamp: Date.now(),
    }
    this.dateFrom = this.state.valueFrom
    this.dateTo = this.state.valueTo

    this.isFullScreenDefault = !this.state.fullScreen

    this.ref = React.createRef()
    this.containerRef = React.createRef()
    this.refInputFirst = React.createRef()
    this.refInputSecond = React.createRef()

    this.el = document.createElement('div')

    this.changeFrame = this.changeFrame.bind(this)
    this.changeNavigatorTimeline = this.changeNavigatorTimeline.bind(this)
    this.setExtremes = this.setExtremes.bind(this)
    this.setDates = this.setDates.bind(this)
    this.handleOrientationChange = this.handleOrientationChange.bind(this)
    this.handleFullScreen = this.handleFullScreen.bind(this)
  }

  componentDidUpdate(prevProps: Props) {
    const { minDate } = this.props
    const { minDate: minDatePrev } = prevProps
    if (minDate !== minDatePrev && minDate) {
      this.dateFrom = formatDateToInputFormat(minDate)
      this.setState({
        valueFrom: formatDateToInputFormat(minDate),
      })
    }
  }

  async componentDidMount() {
    const { id, minDate } = this.props
    this.setState({ isServer: false })
    // setTimeout(() => {
    //   this.setState({
    //     isChartHidden: false,
    //     isServer: false,
    //   })
    // }, this.props.waitBeforeShow)

    if ('onorientationchange' in window) {
      window.addEventListener('orientationchange', this.handleOrientationChange)
    }

    document.addEventListener('keypress', this.handleKeyPress, true)
    document.addEventListener('keydown', this.handleKeyDown, true)
    document.addEventListener('mousedown', this.handleOnMouseDown)
    document.body.appendChild(this.el)

    const rawDisabledCharts = localStorage.getItem(`${id}_enabledCharts`)
    const disabledCharts = rawDisabledCharts ? rawDisabledCharts.split(',') : []

    if (minDate) {
      this.dateFrom = formatDateToInputFormat(minDate)
      this.setState({
        valueFrom: formatDateToInputFormat(minDate),
        disabledCharts,
      })
    } else {
      this.setState({
        disabledCharts,
      })
    }

    await this.initialDataRequest()

    setTimeout(() => {
      if (this.ref.current && this.ref.current.chart) {
        this.ref.current.chart.reflow()
      }
    }, 300)
  }

  initialDataRequest = async () => {
    const { navigatorData } = this.props

    this.setExtremes(navigatorData)
  }

  componentWillUnmount() {
    window.removeEventListener(
      'orientationchange',
      this.handleOrientationChange
    )
    document.removeEventListener('keypress', this.handleKeyPress, true)
    document.removeEventListener('keydown', this.handleKeyDown, true)
    document.addEventListener('mousedown', this.handleOnMouseDown)
    if (this.el) {
      const modalRoot = document.getElementById('modal-root')
      if (modalRoot) {
        modalRoot.removeChild(this.el)
      }
    }
    if (this.afterSetExtremesTimeout) {
      window.cancelAnimationFrame(this.afterSetExtremesTimeout)
    }
  }

  async changeFrame(frame: string, period: string) {
    const timestampTo = Date.now()
    let timestampFrom = getDateInterval(frame)

    if (period === 'all') {
      timestampFrom = wholePeriodTimestampFrom
    }

    const { id } = this.props
    combinedAnalytics(
      'Filter',
      'Filter',
      `Interaction:chart page ${id === 'coinChart' ? 'total' : 'dominance'}`,
      `Time range:${period}`
    )

    const results = await this.props.onDateChange(timestampFrom, timestampTo)
    this.setExtremes(results)

    this.setState(() => ({
      choosenFrame: frame,
    }))
  }

  changeNavigatorTimeline(event: any) {
    if (event.trigger == 'navigator' || event.trigger == 'zoom') {
      if (this.afterSetExtremesTimeout) {
        window.cancelAnimationFrame(this.afterSetExtremesTimeout)
      }

      this.afterSetExtremesTimeout = window.requestAnimationFrame(async () => {
        const timestampFrom = Math.round(event.min)
        const timestampTo = Math.round(event.max)
        const valueFrom = formatDateToInputFormat(timestampFrom)
        const valueTo = formatDateToInputFormat(timestampTo)
        this.props.onDateChange(timestampFrom, timestampTo)

        const { id } = this.props
        combinedAnalytics(
          'Filter',
          'Filter',
          `Interaction:chart page ${
            id === 'coinChart' ? 'total' : 'dominance'
          }`,
          `Period:${valueFrom}_${valueTo}`
        )

        const frame = getFramByInteral(timestampTo - timestampFrom)

        this.setState({
          valueTo,
          valueFrom,
          choosenFrame: frame,
        })
      })
    }
  }

  isMobileLandscape() {
    const { isMobile } = this.props
    if (typeof window === 'undefined') return false
    if (!window?.screen?.orientation) return false

    const { angle } = screen.orientation
    return angle !== 0 && angle !== 180 && isMobile
  }

  getWidth() {
    const { width } = this.props
    const { fullScreen } = this.state
    return fullScreen
      ? window.innerWidth
      : this.containerRef.current
      ? this.containerRef.current.scrollWidth
      : width
  }

  getHeight() {
    const { height, isMobile } = this.props
    const { fullScreen } = this.state

    let finalHeight
    if (isMobile) {
      finalHeight = height < 360 ? 360 : height
    } else {
      finalHeight = height < 400 ? 400 : height
    }
    false
    return fullScreen ? window.innerHeight : finalHeight
  }

  getMaxX(data: DataProvider[]) {
    const { DataProviders } = this.props
    const DataProvider = data || DataProviders
    const dataLength = DataProvider.length

    return get(DataProvider, [dataLength - 1, 'x'], 0)
  }

  getMinX(data: DataProvider[]) {
    const { DataProviders } = this.props
    const DataProvider = data || DataProviders

    return get(DataProvider, ['0', 'x'], 0)
  }

  setDates(from: number, to: number) {
    this.dateFrom = formatDateToInputFormat(from)
    this.dateTo = formatDateToInputFormat(to)
    this.setState({
      valueFrom: this.dateFrom,
      valueTo: this.dateTo,
    })
  }

  handleOnFocus =
    (type: formType) => (e: React.FocusEvent<HTMLInputElement>) => {
      const { valueFromOldValue, valueToOldValue } = this.state
      const stateObj = {
        inputInFocus: type,
        valueFromOldValue,
        valueToOldValue,
      }

      if (type === formType.valueFrom) {
        stateObj.valueFromOldValue = e.target.value
      } else {
        stateObj.valueToOldValue = e.target.value
      }
      this.setState(stateObj)
    }

  validateFullDateString = (str: string) => {
    const d = new Date(str)
    const timestamp = d.getTime()
    return (
      /^(20|21)[0-9]{2}\-(1[0-2]|0?[1-9])\-([1-2][0-9]|3[0-1]|0?[1-9])$/g.test(
        str
      ) &&
      !Number.isNaN(timestamp) &&
      timestamp < Date.now()
    )
  }

  handleOnBlur = () => {
    const { valueFrom, valueTo, defaultFromTimestamp, defaultToTimestamp } =
      this.state
    const timeFrom = new Date(valueFrom)
    const timeTo = new Date(valueTo)
    const timestampFrom = timeFrom.getTime()
    const timestampTo = timeTo.getTime()

    if (
      timestampFrom > timestampTo ||
      timestampFrom < defaultFromTimestamp ||
      defaultToTimestamp < timestampTo
    ) {
      this.setState({
        valueFrom: this.state[formTypeOld.valueFromOldValue],
        valueTo: this.state[formTypeOld.valueToOldValue],
      })
      return
    }

    if (
      !this.validateFullDateString(valueFrom) ||
      !this.validateFullDateString(valueTo)
    ) {
      this.setState({
        valueFrom: this.dateFrom,
        valueTo: this.dateTo,
      })
      return
    }

    if (isNaN(timestampFrom) || isNaN(timestampTo)) {
      // @ts-ignore
      this.setState({
        valueFrom: this.dateFrom,
        valueTo: this.dateTo,
      })

      return
    }

    if (
      this.state[formTypeOld.valueFromOldValue] !==
      this.state[formType.valueFrom]
    ) {
      this.setState(
        {
          [formTypeOld.valueFromOldValue]: null,
          inputInFocus: null,
        },
        async () => {
          const results = (await this.props.onDateChange(
            timestampFrom,
            timestampTo
          )) as any
          this.setExtremes(results)
        }
      )
    } else if (
      this.state[formTypeOld.valueToOldValue] !== this.state[formType.valueTo]
    ) {
      this.setState(
        {
          [formTypeOld.valueToOldValue]: null,
          inputInFocus: null,
        },
        async () => {
          const results = (await this.props.onDateChange(
            timestampFrom,
            timestampTo
          )) as any
          this.setExtremes(results)
        }
      )
    }
  }

  handleOnMouseDown = (e: any) => {
    const { inputInFocus } = this.state
    const couldRunClickHandler =
      !e.target.className.baseVal ||
      (!e.target.className.baseVal.includes('navigator') &&
        !e.target.className.baseVal.includes('scrollbar'))
    if (couldRunClickHandler) {
      if (inputInFocus !== null && !e.target.matches('input')) {
        const input = document.getElementById(inputInFocus)
        if (input) {
          input.blur()
        }
        if (e.target.matches('.Chart-button-period')) {
          const state: any = {
            inputInFocus: null,
          }
          if (inputInFocus === formType.valueFrom) {
            state[formType.valueFrom] =
              this.state[formTypeOld.valueFromOldValue]
          } else {
            state[formType.valueTo] = this.state[formTypeOld.valueToOldValue]
          }
          this.setState(state)
        } else {
          this.handleOnBlur()
        }
      }
    }
  }

  isValidDateString(str: string) {
    return /^[0-9-]*$/g.test(str) || str === ''
  }

  onChangeHandler = (type: formType) => (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    //TO DO: WHAT?

    if (!this.isValidDateString(e.target.value)) {
      return
    }

    // @ts-ignore
    this.setState({
      [type]: e.target.value,
    })
  }

  toggleSeriesVisibiblity = (type: string) => () => {
    const { disabledCharts } = this.state
    const { id, charts } = this.props
    let isEnabled: boolean
    let newDisabledCharts: string[]

    if (disabledCharts.includes(type)) {
      newDisabledCharts = disabledCharts.filter(i => i !== type)
      isEnabled = true
    } else {
      newDisabledCharts = disabledCharts.map(i => i)
      newDisabledCharts.push(type)
      isEnabled = false
    }
    this.setState(
      {
        disabledCharts: newDisabledCharts,
      },
      () => {
        localStorage.setItem(`${id}_enabledCharts`, newDisabledCharts.join(','))

        if (this.ref.current && this.ref.current.chart) {
          const updateConfig = charts
            .filter(i => i.isAxis)
            .map(i =>
              i.name === type
                ? {
                    labels: {
                      enabled: isEnabled,
                    },
                    title: {
                      text: isEnabled ? i.title : '',
                    },
                  }
                : {}
            )

          this.ref.current.chart.update({
            yAxis: updateConfig,
          })
        }
      }
    )
  }

  onKeyDownInput = (e: any) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      this.handleOnBlur()
    }
  }

  setExtremes(DataProvider: DataProvider[], shouldSetDates = true) {
    if (!this.ref.current) return
    const { chart } = this.ref.current
    const xAxis = chart.xAxis[0]

    const minX = this.getMinX(DataProvider)
    const maxX = this.getMaxX(DataProvider)

    if (shouldSetDates) {
      this.setDates(minX, maxX)
    }

    xAxis.setExtremes(minX, maxX)
  }

  handleOrientationChange() {
    if (this.isMobileLandscape()) {
      if (!this.state.fullScreen) {
        this.isFullScreenDefault = false
      }
      this.setState({
        fullScreen: true,
      })
    } else if (!this.isFullScreenDefault) {
      this.isFullScreenDefault = true
      this.setState({
        fullScreen: false,
      })
    }
    setTimeout(() => {
      const { chart } = this.ref.current
      chart.setSize(this.getWidth(), this.getHeight())
    }, 1000)
  }

  handleKeyPress = (event: any) => {
    const { fullScreen } = this.state
    if (event.code === 'KeyF' || event.keyCode === 70 || event.which === 70) {
      event.stopPropagation()
      if (event.target && !event.target.matches('input')) {
        combinedAnalytics(
          'Keyboard',
          'Keyboard',
          'F',
          fullScreen ? 'Off' : 'On'
        )
        this.handleFullScreen()
      }
    }
  }

  handleKeyDown = (event: any) => {
    if (
      this.state.fullScreen &&
      (event.key === 'Escape' || event.keyCode === 27 || event.which === 27)
    ) {
      event.stopPropagation()
      this.handleFullScreen()
    }
  }

  handleFullScreen() {
    const { fullScreen } = this.state
    this.setState({ fullScreen: !fullScreen })
  }

  handleFullScreenBtnClick() {
    const { fullScreen } = this.state

    const { id } = this.props
    combinedAnalytics(
      'Button',
      'Button',
      `Fullscreen Chart ${id === 'coinChart' ? 'Total' : 'Dominance'}`,
      fullScreen ? 'Off' : 'On'
    )

    this.handleFullScreen()
  }

  exportChart = (type: any, options: any) => {
    const { choosenFrame } = this.state
    const { id } = this.props
    this.ref.current.chart.exportChart(type, options)
    combinedAnalytics(
      'Downloads',
      'Downloads',
      `${id}`,
      `${id.toLowerCase()}-${choosenFrame}.png`
    )
  }

  getExportHandlers = () => {
    return [
      {
        text: 'Download PNG',
        onclick: (options: any) => {
          this.exportChart({ type: 'image/png' }, options)
        },
      },
      {
        text: 'Download JPEG',
        onclick: (options: any) => {
          this.exportChart({ type: 'image/jpeg' }, options)
        },
      },
      // {
      //   text: 'Download SVG',
      //   onclick: (options: any) => {
      //     this.exportChart({ type: 'image/svg+xml' }, options)
      //   },
      // },
    ]
  }

  setupConfig(): any {
    const { DataProviders, generateConfig, navigatorData, isMobile } =
      this.props
    const { choosenFrame, fullScreen, disabledCharts } = this.state
    const config = generateConfig(
      DataProviders,
      navigatorData,
      disabledCharts,
      choosenFrame,
      fullScreen,
      this.getHeight(),
      this.getWidth()
    )

    if (isMobile) {
      if (config.scrollbar) {
        config.scrollbar.enabled = false
      }

      if (config.navigator) {
        config.navigator.enabled = false
      }
    }

    config.xAxis[0].events = {
      afterSetExtremes: this.changeNavigatorTimeline,
    }

    return config
  }

  renderChart() {
    const { choosenFrame, valueFrom, valueTo, fullScreen, disabledCharts } =
      this.state

    const {
      isMobile,
      isShare,
      DataProviders,
      charts,
      zoomPadding,
      mobileZoomPadding,
      additionalButtons,
      controlMarginRight,
      frames,
      isDownloadDropdownInverted,
    } = this.props

    const config = this.setupConfig()

    const data = DataProviders
    const isEmptyData =
      !data[0] || !data[0].length || disabledCharts.length === data.length

    const containerStyle: React.CSSProperties = {
      ...(fullScreen
        ? {
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 300,
          }
        : {}),
      width: '100%',
    }

    return (
      <div
        className='Chart-container'
        style={containerStyle}
        ref={this.containerRef}
      >
        <div
          className='Chart-control'
          style={{
            width: isMobile ? 'calc(100% - 30px)' : '100%',
            ...(fullScreen
              ? {
                  top: 10,
                }
              : {}),
          }}
        >
          <div
            className='Chart-buttons-container'
            style={{ marginLeft: isMobile ? mobileZoomPadding : zoomPadding }}
          >
            {!isMobile && <div className='Chart-period-zoom-label'>Zoom</div>}
            <div className='Chart-period-buttons'>
              {(frames || DEFAULT_FRAMES).map(({ period, frame }) => (
                <button
                  key={period}
                  onClick={() => this.changeFrame(frame, period)}
                  className={`Chart-button-period${
                    choosenFrame === frame ? ' Chart-button-period-active' : ''
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          <div
            className='Chart-control-right'
            style={isMobile ? {} : { marginRight: controlMarginRight }}
          >
            {isMobile || isShare ? null : (
              <form
                className='Chart-period-inputs'
                onKeyDown={this.onKeyDownInput}
                onSubmit={e => {
                  e.preventDefault()
                }}
                action=''
              >
                <label className='Chart-period-label'>
                  {'From '}
                  <input
                    id='valueFrom'
                    className='Chart-period-input'
                    value={valueFrom}
                    onChange={this.onChangeHandler(formType.valueFrom)}
                    onFocus={this.handleOnFocus(formType.valueFrom)}
                    ref={this.refInputFirst}
                  />
                </label>
                <label className='Chart-period-label'>
                  {'To '}
                  <input
                    id='valueTo'
                    className='Chart-period-input'
                    value={valueTo}
                    onChange={this.onChangeHandler(formType.valueTo)}
                    onFocus={this.handleOnFocus(formType.valueTo)}
                    ref={this.refInputSecond}
                  />
                </label>
                <input type='submit' style={{ display: 'none' }} />
              </form>
            )}
            {this.isMobileLandscape() ? null : (
              <span
                className='Chart-fullscreen-btn'
                onClick={() => this.handleFullScreenBtnClick()}
              />
            )}
            {!isMobile ? (
              <ExportMenu
                handlers={this.getExportHandlers()}
                from={valueFrom}
                to={valueTo}
                isMobile={isMobile}
                yAxisConf={config.yAxis}
                isFullScreen={fullScreen}
                choosenFrame={choosenFrame}
                generateExportConfig={this.props.generateExportConfig}
                isAlwaysInverted={isDownloadDropdownInverted}
              />
            ) : null}
          </div>
        </div>
        {isEmptyData ? (
          <div className='Chart-no-data' style={{ height: this.getHeight() }}>
            No data
          </div>
        ) : config?.series.length > 0 ? (
          <div className='Chart-box'>
            <HighchartsReact
              ref={this.ref}
              highcharts={Highcharts}
              constructorType='stockChart'
              options={config}
            />
          </div>
        ) : (
          <div className='Chart-no-data' style={{ height: this.getHeight() }}>
            No data
          </div>
        )}
        <div className='Chart-legend__wrapper'>
          <div className='Chart-legend-list'>
            {charts
              .map(i => ({ ...i }))
              .sort((i, j) => i.labelOrder - j.labelOrder)
              .map(i => (
                <div
                  key={i.name}
                  className={`Chart-legend__item${
                    i.labelClassName ? ` ${i.labelClassName}` : ''
                  }${
                    !disabledCharts.includes(i.name) && i.labelActiveClassName
                      ? ` ${i.labelActiveClassName}`
                      : ''
                  }`}
                  onClick={this.toggleSeriesVisibiblity(i.name)}
                >
                  {i.label}
                </div>
              ))}
          </div>
          {additionalButtons && additionalButtons.length && (
            <div className='Chart-additional'>{additionalButtons}</div>
          )}
        </div>
      </div>
    )
  }

  render() {
    const { fullScreen } = this.state
    const chart = <div className={styles.root}>{this.renderChart()}</div>

    return fullScreen ? createPortal(chart, this.el) : chart
  }
}
